import { Box, Button, Link, Typography } from '@mui/material';
import { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-pro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import isEqual from 'lodash.isequal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { FilterParam } from 'common/src/query-params/FilterParam';
import { ArrayOperators } from 'common/src/query-params/operators';
import { BASE } from 'common/src/routes/BaseRoutes';
import { hiddenAlertSubtypes } from 'common/src/types/enums/AlertSubtype.enum';
import type { IAlert } from 'common/src/types/interfaces';

import { getAlerts } from 'portal/src/api/AlertsService';
import { updateUserService } from 'portal/src/api/UsersService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import useAuth from 'portal/src/auth/useAuth';
import DataTable from 'portal/src/components/common/DataTable';

import AlertDetailPane from './AlertDetailPane';
import getAlertTableColumns from './AlertsTable.columns';

import {
  getEnabledAlertSubtypesAndFilter,
  getSubtypeFilterOptions,
} from './utils/alertsFiltering';
import {
  getApiActiveFilters,
  getNonEmptyFilterItems,
  normalizeFilterItems,
} from './utils/alertsTableFilterUtils';
import { getNoAlertsDataDisplay } from './utils/alertsTableNoDataDisplay';

interface PageState {
  page: number;
  pageSize: number;
}

const AlertsTable = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: selectedAlertId } = useParams<{ id?: string }>();
  const { getUser, refreshUser } = useAuth();
  const user = getUser();
  const hasUpdatedAlertsViewedAtRef = useRef(false);

  const { enabledSubtypes, subtypeFilter } = useMemo(
    () => getEnabledAlertSubtypesAndFilter(user.alertPreferences),
    [user.alertPreferences],
  );
  const hasEnabledSubtypes = enabledSubtypes.length > 0;

  const defaultFilterItems = useMemo<GridFilterItem[]>(() => {
    const filters: GridFilterItem[] = [];

    if (hasEnabledSubtypes) {
      filters.push({
        ...subtypeFilter,
        id: 'default-subtype-preferences',
      });
    }

    return filters;
  }, [hasEnabledSubtypes, subtypeFilter]);

  const [filterState, setFilterState] = useState<GridFilterModel>({
    items: defaultFilterItems,
  });
  const [pageState, setPageState] = useState<PageState>({
    page: 0,
    pageSize: 100,
  });

  const subtypeFilterOptions = useMemo(
    () => getSubtypeFilterOptions(enabledSubtypes),
    [enabledSubtypes],
  );

  const handleResetFilters = useCallback(() => {
    const normalizedItems = normalizeFilterItems(defaultFilterItems);

    setFilterState({ items: normalizedItems });
    setPageState((old) => ({ ...old, page: 0 }));
  }, [defaultFilterItems]);

  const toolbarPreferenceMessage = useMemo(() => {
    const selectedSubtypeValues = filterState.items
      .filter((item) => item.field === 'subtype' && Array.isArray(item.value))
      .flatMap((item) => item.value as string[])
      .filter(Boolean);

    const normalizedSelectedSubtypes = [
      ...new Set(selectedSubtypeValues),
    ].sort();
    const normalizedEnabledSubtypes = [...enabledSubtypes].sort();
    const matchesPreferences =
      normalizedEnabledSubtypes.length > 0 &&
      isEqual(normalizedSelectedSubtypes, normalizedEnabledSubtypes);

    if (matchesPreferences) {
      return (
        <Typography sx={{ mr: 'auto' }} variant="body2">
          (Viewing alerts filtered by user preferences)
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          mr: 'auto',
        }}
      >
        <Typography variant="body2">(</Typography>
        <Link
          component={RouterLink}
          to={BASE.USER_SETTINGS}
          underline="always"
          variant="body2"
        >
          Set alert preferences
        </Link>
        <Typography variant="body2">to match current filters or</Typography>
        <Button
          size="small"
          sx={{
            minWidth: 'auto',
            p: 0,
            textTransform: 'none',
            textDecoration: 'underline',
          }}
          variant="text"
          onClick={handleResetFilters}
        >
          reset filters
        </Button>
        <Typography variant="body2">)</Typography>
      </Box>
    );
  }, [enabledSubtypes, filterState.items, handleResetFilters]);

  const requestFilters = useMemo(() => {
    // get filter items from state
    const items = getApiActiveFilters(
      getNonEmptyFilterItems(filterState.items) as FilterParam[],
    );

    // always append a filter to exclude hidden alert subtypes.
    // this filter is added in the client so that we can still fetch all alerts from the API
    return [
      ...items,
      {
        field: 'subtype',
        operator: ArrayOperators.NOT_IN,
        value: [...hiddenAlertSubtypes],
      },
    ];
  }, [filterState.items]);

  const { data: alertsData, isFetching } = useQuery({
    queryKey: [QUERY_KEYS.alerts, pageState, requestFilters],
    enabled: hasEnabledSubtypes,
    queryFn: async () =>
      getAlerts({
        limit: pageState.pageSize,
        page: pageState.page,
        sort: 'createdAt',
        sortDirection: 'desc',
        filters: requestFilters,
      }),
  });

  useEffect(() => {
    if (!user?.id || hasUpdatedAlertsViewedAtRef.current) {
      return;
    }

    hasUpdatedAlertsViewedAtRef.current = true;

    const viewedAt = new Date().toISOString();

    void (async () => {
      try {
        await updateUserService(user.id, { alertsViewedAt: viewedAt });
        await refreshUser();
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.alertsBadgeCount],
        });
      } catch {
        hasUpdatedAlertsViewedAtRef.current = false;
      }
    })();
  }, [queryClient, refreshUser, user?.id]);

  const alerts: IAlert[] = useMemo(
    () => (hasEnabledSubtypes ? (alertsData?.data ?? []) : []),
    [alertsData, hasEnabledSubtypes],
  );
  const totalCount = hasEnabledSubtypes
    ? (alertsData?.meta?.totalCount ?? 0)
    : 0;
  const selectedAlert = useMemo(
    () => alerts.find((a) => a.id === selectedAlertId) ?? null,
    [alerts, selectedAlertId],
  );

  useEffect(() => {
    if (isFetching) {
      return;
    }

    const firstAlertId = alerts[0]?.id;

    if (!selectedAlertId) {
      if (firstAlertId) {
        navigate(`${BASE.ALERTS}/${firstAlertId}`, { replace: true });
      }
      return;
    }

    if (!selectedAlert && firstAlertId) {
      navigate(`${BASE.ALERTS}/${firstAlertId}`, { replace: true });
      return;
    }

    if (!selectedAlert && alerts.length === 0) {
      navigate(BASE.ALERTS, { replace: true });
    }
  }, [alerts, isFetching, navigate, selectedAlert, selectedAlertId]);

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      const normalizedItems = normalizeFilterItems(model.items);

      if (isEqual(filterState.items, normalizedItems)) {
        return;
      }

      const oldActiveItems = getNonEmptyFilterItems(filterState.items);
      const newActiveItems = getNonEmptyFilterItems(normalizedItems);

      setFilterState({ ...model, items: normalizedItems });
      if (!isEqual(oldActiveItems, newActiveItems)) {
        setPageState((old) => ({ ...old, page: 0 }));
      }
    },
    [filterState.items],
  );

  const handleRowClick = useCallback(
    (params: any) => {
      navigate(`${BASE.ALERTS}/${params.id}`);
    },
    [navigate],
  );

  const noDataDisplay = useMemo(
    () =>
      getNoAlertsDataDisplay({
        filterItemsCount: filterState.items.length,
        hasEnabledSubtypes,
      }),
    [filterState.items, hasEnabledSubtypes],
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        gridTemplateColumns: selectedAlertId ? '1.6fr 1fr' : '1fr',
      }}
    >
      <DataTable
        checkboxSelection={false}
        columns={getAlertTableColumns(subtypeFilterOptions)}
        contextName={BASE.ALERTS}
        columnVisibilityModel={{
          message: false,
          resolutionReason: false,
          resolutionUrl: false,
        }}
        disableMultipleRowSelection
        filterMode="server"
        filterModel={filterState}
        loading={isFetching}
        noDataDisplay={noDataDisplay}
        onFilterModelChange={handleFilterModelChange}
        onPaginationModelChange={(model, { reason }) => {
          if (reason === 'setPaginationModel') {
            setPageState((old) => {
              const page = model.pageSize !== old.pageSize ? 0 : model.page;
              return { ...old, page, pageSize: model.pageSize };
            });
          }
        }}
        onRowClick={handleRowClick}
        onRowDoubleClick={() => {}}
        paginationMode="server"
        paginationModel={{
          page: pageState.page,
          pageSize: pageState.pageSize,
        }}
        rows={alerts}
        rowCount={totalCount}
        sortingMode="server"
        toolbarSlotProps={{
          csvOptions: { allColumns: true },
          exportFileName: 'alerts',
          leftSideChildren: toolbarPreferenceMessage,
          showFilters: true,
        }}
      />
      {selectedAlertId && (
        <AlertDetailPane alert={selectedAlert} loading={isFetching} />
      )}
    </Box>
  );
};

export default AlertsTable;
